/** The finishing / handling / cleaning catalog, and what it resolves to per job.
 *
 * Three kinds share one catalog because they share one question: does this job
 * need something done to the parts beyond machining, and if so, in what size
 * batches. Finishing is derived from the surface finish on the line item;
 * handling and cleaning are picked.
 */

export const PROCESS_KINDS = ['finishing', 'handling', 'cleaning'] as const;
export type ProcessKind = (typeof PROCESS_KINDS)[number];

export const PROCESS_LABELS: Record<ProcessKind, string> = {
	finishing: 'Finishing',
	handling: 'Handling',
	cleaning: 'Cleaning',
};

/** `do_nothing` is the ordinary case: no tag, no badge, no operator prompt. */
export type ProcessBehavior = 'do_nothing' | 'special';

/** `none` prints no tags; `one_tag` is one tag for the job; `quality_interval` is one per batch. */
export type ProcessBatchMode = 'none' | 'one_tag' | 'quality_interval';

/** How much instruction text fits on a tag before it runs off the print area.
 *  Must match `INSTRUCTIONS_MAX_LENGTH` in `orders/process_catalog.py`. */
export const INSTRUCTIONS_MAX_LENGTH = 240;

/** A catalog row, as the admin screen edits it. */
export interface ProcessSpec {
	id: string;
	kind: ProcessKind;
	name: string;
	behavior: ProcessBehavior;
	/** Finishing only: surface-finish text that resolves to this row. */
	match_tokens: string[];
	instructions: string;
	batch_mode: ProcessBatchMode;
	is_default: boolean;
	is_active: boolean;
	sort_order: number;
	is_special: boolean;
	prints_tags: boolean;
}

export type ProcessSpecWrite = Partial<
	Omit<ProcessSpec, 'id' | 'is_special' | 'prints_tags'>
>;

/** The nested shape wherever a line item or order points at a catalog row. */
export interface ProcessSpecRef {
	id: string;
	kind: ProcessKind;
	name: string;
	behavior: ProcessBehavior;
	is_special: boolean;
	batch_mode: ProcessBatchMode;
	is_active?: boolean;
}

/** What a line item actually needs, resolved. */
export interface ProcessRequirement {
	spec_id: string;
	name: string;
	is_special: boolean;
	instructions: string;
	batch_mode: ProcessBatchMode;
	/** Good-part counts at which each batch comes due. Empty when unbatched. */
	thresholds: number[];
	batch_sizes: number[];
}

/** Keyed by kind. `null` means nothing resolved for that kind on this job. */
export type ProcessRequirements = Partial<
	Record<ProcessKind, ProcessRequirement | null>
>;

/** The special cleaning the delivery board gates on, if any. */
export interface CleaningRequirement {
	name: string;
	instructions: string;
}

/** "3 batches of 20 (last 8)" — how the batching reads on screen. */
export function describeBatches(requirement: ProcessRequirement): string {
	const sizes = requirement.batch_sizes;
	if (sizes.length === 0) return '';
	if (sizes.length === 1) return `1 batch of ${sizes[0]}`;

	const first = sizes[0];
	const last = sizes[sizes.length - 1];
	const tail = last !== first ? ` (last ${last})` : '';
	return `${sizes.length} batches of ${first}${tail}`;
}
